/**
 * Capitalize the FIRST letter of a formatted date label — safe for accented
 * characters. Despite the name (kept for its ~50 callers) it no longer touches
 * later words: Spanish month/weekday words after the first stay lowercase
 * ("Mié, 14 oct", "14 de octubre de 2026", never "14 Oct" / "14 De Octubre").
 * English output from Intl is already capitalized ("Tue, Jul 14"), so it is
 * unchanged.
 */
export const capitalizeWords = (str) =>
  str ? str.charAt(0).toLocaleUpperCase() + str.slice(1) : str;

/** Capitalize ONLY the first letter ("octubre de 2026" -> "Octubre de 2026"; English unchanged). */
export const capitalizeFirst = (str) => (str ? str.charAt(0).toLocaleUpperCase() + str.slice(1) : str);

/** Calendar month title: es "Octubre de 2026", en "October 2026". */
export const formatMonthYearTitle = (date, locale) =>
  capitalizeFirst(date.toLocaleString(locale, { month: 'long', year: 'numeric' }));

export const localDateStr = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const getDayName = (day, intl) => {
  if (day === null || day === undefined) return '—';
  if (intl) {
    const keys = ['common.daySunFull','common.dayMonFull','common.dayTueFull','common.dayWedFull','common.dayThuFull','common.dayFriFull','common.daySatFull'];
    const defaults = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    return intl.formatMessage({ id: keys[day], defaultMessage: defaults[day] });
  }
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  return days[day] ?? '—';
};

/** A weekday label placed INSIDE a sentence or after other words ("Día de servicio: lunes").
 *  Spanish weekdays are lowercase there (rulebook: sentence case); a label that is just the
 *  day keeps its capital via `getDayName`. English weekdays always stay capitalized. */
export const weekdayInSentence = (label, intl) => (
  label && String(intl?.locale || '').toLowerCase().startsWith('es') ? String(label).toLocaleLowerCase('es') : label
);

export const getDayNameInline = (day, intl) => weekdayInSentence(getDayName(day, intl), intl);

export const getDayNameShort = (day, intl) => {
  if (day === null || day === undefined) return '—';
  if (intl) {
    const keys = ['common.daySun','common.dayMon','common.dayTue','common.dayWed','common.dayThu','common.dayFri','common.daySat'];
    const defaults = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return intl.formatMessage({ id: keys[day], defaultMessage: defaults[day] });
  }
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return days[day] ?? '—';
};

// Short weekday+month+day, capitalized for ES consistency ("Jue, 2 Jul").
// A CALENDAR date → noon-anchored, browser-local, NO timeZone (per the tz contract).
export const formatShortWeekdayDate = (iso, locale) => {
  if (!iso) return '';
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
    return capitalizeWords(d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' }));
  } catch { return String(iso); }
};


